// ENHANCED IMAGE ANALYSIS - Objects, Scene, Colors, Gender Detection
import * as cocoSsd from '@tensorflow-models/coco-ssd'
import * as mobilenet from '@tensorflow-models/mobilenet'
import '@tensorflow/tfjs'

let cocoModel: cocoSsd.ObjectDetection | null = null
let mobileNetModel: mobilenet.MobileNet | null = null

export interface EnhancedAnalysisResult {
  objects: string[]
  objectDetails: Array<{ class: string; score: number; bbox: number[] }>
  scene: string
  sceneConfidence: number
  detectedText: string[]
  hasText: boolean
  dominantColors: string[]
  colorMood: string
  // Top 4-5 tags shown in UI (high confidence)
  keywords: string[]
  displayKeywords: string[]
  displayKeywordsWithConfidence: Array<{ keyword: string; confidence: number }>
  // All detected keywords including low-confidence (used for caption generation with less weight)
  allDetectedKeywords: string[]
  lowConfidenceKeywords: string[]
  // Gender detection
  gender: 'male' | 'female' | 'multiple' | null
  genderConfidence: number
  confidence: number
  mood: string
  category: string
}

export async function initializeEnhancedAnalysis(): Promise<void> {
  try {
    console.log('🔍 Loading analysis models...')
    
    // On mobile, only load COCO-SSD — MobileNet + Tesseract are too heavy
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    
    // Disable IndexedDB model caching upfront — fixes incognito/private browsing
    // where Chrome blocks IndexedDB and TF.js throws trying to cache model weights
    const tf = await import('@tensorflow/tfjs')
    await tf.ready()
    // Tell TF.js not to use IndexedDB for model storage
    tf.env().set('IS_BROWSER', true)

    // Load COCO-SSD — try specific base first, fall back to default
    try {
      cocoModel = await cocoSsd.load({ base: isMobile ? 'lite_mobilenet_v2' : 'mobilenet_v2' } as any)
    } catch {
      cocoModel = await cocoSsd.load()
    }
    console.log('✅ COCO-SSD loaded')
    
    // Mobile: COCO-SSD only (MobileNet causes memory crash on mobile)
    // Desktop: load both for better scene detection
    if (!isMobile) {
      try {
        mobileNetModel = await mobilenet.load()
        console.log('✅ MobileNet loaded')
      } catch (e) {
        console.warn('⚠️ MobileNet failed, scene detection disabled:', e)
        mobileNetModel = null
      }
    } else {
      console.log('📱 Mobile — skipping MobileNet to prevent memory crash')
      mobileNetModel = null
    }
    
    console.log('✅ Models ready')
  } catch (error) {
    console.error('❌ Failed to load models:', error)
    throw error
  }
}

// Gender detection disabled — face-api.js v0.22 is incompatible with TF.js v4
// (throws uncatchable TypeError: f/h is not a function inside TF.js internals)

export async function analyzeImageEnhanced(imageFile: File): Promise<EnhancedAnalysisResult> {
  if (!cocoModel) {
    throw new Error('Models not initialized')
  }

  const startTime = Date.now()
  const imageElement = await createImageElement(imageFile)

  // On mobile: only run COCO-SSD + colors (skip MobileNet scene + gender to save memory)
  const [objectResults, sceneResults, colorResults] = await Promise.all([
    analyzeObjects(imageElement),
    mobileNetModel ? analyzeScene(imageElement) : Promise.resolve({ scene: 'lifestyle', confidence: 0 }),
    analyzeColors(imageElement),
  ])
  // Gender detection disabled — face-api.js v0.22 is incompatible with TF.js v4
  const genderResult = { gender: null as null, genderConfidence: 0 }

  // Scene inference from objects when MobileNet returns 'lifestyle' or is unavailable
  let finalScene = sceneResults.scene
  if (finalScene === 'lifestyle') {
    const objs = objectResults.allKeywords
    // Food items take highest priority
    if (objs.some(o => ['apple', 'banana', 'orange', 'pizza', 'cake', 'donut', 'sandwich', 'carrot', 'broccoli', 'hot dog', 'bowl'].includes(o))) finalScene = 'food'
    else if (objs.some(o => ['cup', 'bottle'].includes(o))) finalScene = 'food'
    else if (objs.some(o => ['plant', 'flower', 'tree'].includes(o))) finalScene = 'plant'
    else if (objs.some(o => ['laptop', 'computer', 'keyboard', 'mouse'].includes(o))) finalScene = 'workspace'
    else if (objs.some(o => ['book'].includes(o))) finalScene = 'books'
    else if (objs.some(o => ['dog', 'cat', 'bird', 'horse', 'elephant', 'bear'].includes(o))) finalScene = 'animal'
    else if (objs.some(o => ['car', 'bike', 'motorcycle', 'bus', 'train'].includes(o))) finalScene = 'transportation'
    else if (objs.some(o => ['tie', 'handbag', 'backpack', 'umbrella'].includes(o))) finalScene = 'fashion'
    // Only use indoor as last resort — don't let furniture override specific content
    else if (objs.some(o => ['sofa', 'bed', 'vase', 'clock'].includes(o))) finalScene = 'indoor'
    // dining table alone = likely food photo, not "indoor"
    else if (objs.some(o => ['dining table'].includes(o))) finalScene = 'food'
    else if (objs.some(o => ['person', 'people', 'man', 'woman'].includes(o))) finalScene = 'lifestyle'
  }

  // Add gender to keywords if detected
  const genderKeywords: string[] = []
  if (genderResult.gender === 'male') genderKeywords.push('man')
  else if (genderResult.gender === 'female') genderKeywords.push('woman')
  else if (genderResult.gender === 'multiple') genderKeywords.push('people')

  // Color mood words are tone descriptors — exclude from subject keywords, keep for colorVibes
  const colorMoodWords = new Set(['energetic', 'moody', 'cozy', 'calm', 'passionate', 'serene', 'fresh', 'vibrant', 'airy', 'balanced', 'neutral', 'lifestyle', 'general'])

  // Color-based content inference — catches things COCO-SSD misses (strawberry, fruits, etc.)
  const colorInferred = inferContentFromColors(imageElement, objectResults.allKeywords)

  // All keywords for caption generation — include scene + extra scenes from MobileNet + color inference
  const sceneForKeywords = finalScene !== 'lifestyle' ? finalScene : null
  const extraSceneKeywords = (sceneResults as any).extraScenes || []
  const allDetectedKeywords = [
    ...objectResults.allKeywords,
    ...genderKeywords,
    ...(sceneForKeywords ? [sceneForKeywords] : []),
    ...extraSceneKeywords,
    ...colorInferred
  ].filter(k => k && !colorMoodWords.has(k.toLowerCase()))

  // High-confidence keywords only (>= 0.45) for display
  const highConfKeywords = objectResults.highConfKeywords
  const lowConfKeywords = objectResults.lowConfKeywords

  // Generic scenes that shouldn't show as display tags
  const genericScenes = new Set(['indoor', 'lifestyle', 'general', 'outdoor'])

  // Build display keywords — color-inferred specific items take priority
  const keywordsWithConf: Array<{ keyword: string; confidence: number }> = []

  // Color-inferred specific items first (strawberry, banana, etc.)
  for (const k of colorInferred) {
    if (!keywordsWithConf.find(x => x.keyword === k)) {
      keywordsWithConf.push({ keyword: k, confidence: 0.7 })
    }
    if (keywordsWithConf.length >= 3) break
  }

  // Scene if meaningful and specific (not generic)
  if (finalScene !== 'lifestyle' && !genericScenes.has(finalScene)) {
    if (!keywordsWithConf.find(k => k.keyword === finalScene)) {
      keywordsWithConf.push({ keyword: finalScene, confidence: sceneResults.confidence })
    }
  }

  // Gender tag
  if (genderResult.gender && genderResult.gender !== 'multiple') {
    keywordsWithConf.push({ keyword: genderResult.gender === 'male' ? 'man' : 'woman', confidence: genderResult.genderConfidence })
  }

  // High-conf COCO objects (≥ 0.25) — skip generic furniture if we have specific items
  const hasSpecificItems = colorInferred.length > 0 || keywordsWithConf.length > 0
  const skipIfGeneric = new Set(['dining table', 'chair', 'couch', 'sofa', 'bed', 'bench'])
  for (const obj of objectResults.details.filter(d => d.score >= 0.25).sort((a, b) => b.score - a.score)) {
    if (hasSpecificItems && skipIfGeneric.has(obj.class)) continue
    if (!keywordsWithConf.find(k => k.keyword === obj.class)) {
      keywordsWithConf.push({ keyword: obj.class, confidence: obj.score })
    }
    if (keywordsWithConf.length >= 5) break
  }

  // Fill remaining slots with low-confidence detections (≥ 0.10)
  if (keywordsWithConf.length < 4) {
    for (const obj of objectResults.details.filter(d => d.score < 0.25 && d.score >= 0.10).sort((a, b) => b.score - a.score)) {
      if (!keywordsWithConf.find(k => k.keyword === obj.class)) {
        keywordsWithConf.push({ keyword: obj.class, confidence: obj.score })
      }
      if (keywordsWithConf.length >= 4) break
    }
  }

  // Add color-inferred keywords to display if we have few COCO detections
  if (keywordsWithConf.length < 3 && colorInferred.length > 0) {
    for (const k of colorInferred) {
      if (!keywordsWithConf.find(x => x.keyword === k)) {
        keywordsWithConf.push({ keyword: k, confidence: 0.6 })
      }
      if (keywordsWithConf.length >= 5) break
    }
  }

  const topKeywordsWithConf = keywordsWithConf.slice(0, 5)
  const displayKeywords = topKeywordsWithConf.map(k => k.keyword)

  const result: EnhancedAnalysisResult = {
    objects: highConfKeywords,
    objectDetails: objectResults.details,
    scene: finalScene,
    sceneConfidence: sceneResults.confidence,
    detectedText: [],
    hasText: false,
    dominantColors: colorResults.colors,
    colorMood: colorResults.mood,
    keywords: [...new Set([...highConfKeywords, finalScene !== 'lifestyle' ? finalScene : ''].filter(k => Boolean(k) && !colorMoodWords.has(k.toLowerCase())))],
    displayKeywords: [...new Set(displayKeywords)],
    displayKeywordsWithConfidence: topKeywordsWithConf,
    allDetectedKeywords: [...new Set(allDetectedKeywords)],
    lowConfidenceKeywords: lowConfKeywords,
    gender: genderResult.gender,
    genderConfidence: genderResult.genderConfidence,
    confidence: (objectResults.avgConfidence + sceneResults.confidence) / 2,
    mood: determineMood(finalScene, colorResults.mood, highConfKeywords),
    category: determineCategory(finalScene, highConfKeywords)
  }

  console.log(`✅ Analysis complete in ${((Date.now() - startTime) / 1000).toFixed(1)}s`, {
    display: displayKeywords,
    gender: genderResult.gender,
    lowConf: lowConfKeywords,
    scene: finalScene
  })

  return result
}

async function analyzeObjects(imageElement: HTMLImageElement) {
  const predictions = await cocoModel!.detect(imageElement)

  // Lower thresholds — plants, laptops, books often score 20-40%
  const highConf = predictions.filter(p => p.score >= 0.25)
  const lowConf = predictions.filter(p => p.score >= 0.10 && p.score < 0.25)

  const buildKeywords = (preds: typeof predictions) => {
    const keywords: string[] = []
    const seen = new Set<string>()
    let personCount = 0
    for (const pred of preds.slice(0, 10)) {
      const normalized = normalizeClassName(pred.class)
      if (normalized === 'person') { personCount++; continue }
      if (!seen.has(normalized)) { keywords.push(normalized); seen.add(normalized) }
    }
    if (personCount === 1) keywords.unshift('person')
    else if (personCount >= 2) keywords.unshift('people')
    return keywords
  }

  const highConfKeywords = buildKeywords(highConf)
  const lowConfKeywords = buildKeywords(lowConf).filter(k => !highConfKeywords.includes(k))
  const allKeywords = [...new Set([...highConfKeywords, ...lowConfKeywords])]

  const avgConfidence = predictions.length > 0
    ? predictions.reduce((s, p) => s + p.score, 0) / predictions.length : 0

  return {
    highConfKeywords,
    lowConfKeywords,
    allKeywords,
    details: predictions.map(p => ({ class: normalizeClassName(p.class), score: p.score, bbox: p.bbox })),
    avgConfidence
  }
}

async function analyzeScene(imageElement: HTMLImageElement) {
  const predictions = await mobileNetModel!.classify(imageElement, 10)
  if (predictions.length === 0) return { scene: 'lifestyle', confidence: 0, extraScenes: [] as string[] }

  // Collect all non-lifestyle scenes from top predictions
  const scenes: Array<{ scene: string; confidence: number }> = []
  for (const pred of predictions) {
    const scene = classifyScene(pred.className)
    if (scene !== 'lifestyle' && !scenes.find(s => s.scene === scene)) {
      scenes.push({ scene, confidence: pred.probability })
    }
  }

  if (scenes.length === 0) {
    return { scene: 'lifestyle', confidence: 0, extraScenes: [] as string[] }
  }

  return {
    scene: scenes[0].scene,
    confidence: scenes[0].confidence,
    extraScenes: scenes.slice(1).map(s => s.scene) // Additional detected scenes
  }
}


async function analyzeColors(imageElement: HTMLImageElement) {
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!
    canvas.width = imageElement.width
    canvas.height = imageElement.height
    ctx.drawImage(imageElement, 0, 0)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const colors = extractDominantColors(imageData)
    const mood = determineColorMood(colors)
    return { colors, mood, keywords: [mood, ...colors.map(c => `${c}-tones`)] }
  } catch {
    return { colors: [] as string[], mood: 'neutral', keywords: [] as string[] }
  }
}

function extractDominantColors(imageData: ImageData): string[] {
  const colorCounts: Record<string, number> = {}
  const pixels = imageData.data
  for (let i = 0; i < pixels.length; i += 40) {
    const name = rgbToColorName(pixels[i], pixels[i + 1], pixels[i + 2])
    colorCounts[name] = (colorCounts[name] || 0) + 1
  }
  return Object.entries(colorCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([c]) => c)
}

function rgbToColorName(r: number, g: number, b: number): string {
  const brightness = (r + g + b) / 3
  if (brightness < 50) return 'dark'
  if (brightness > 200) return 'bright'
  if (r > g && r > b) return r > 150 ? 'red' : 'warm'
  if (g > r && g > b) return g > 150 ? 'green' : 'natural'
  if (b > r && b > g) return b > 150 ? 'blue' : 'cool'
  if (Math.abs(r - g) < 30 && Math.abs(g - b) < 30) return brightness > 150 ? 'light' : 'neutral'
  return 'colorful'
}

function determineColorMood(colors: string[]): string {
  const map: Record<string, string> = { bright: 'energetic', dark: 'moody', warm: 'cozy', cool: 'calm', red: 'passionate', blue: 'serene', green: 'fresh', colorful: 'vibrant', light: 'airy', neutral: 'balanced' }
  return map[colors[0]] || 'neutral'
}

function classifyScene(className: string): string {
  const l = className.toLowerCase()
  // Nature
  if (l.includes('mountain') || l.includes('alp') || l.includes('cliff') || l.includes('peak')) return 'mountains'
  if (l.includes('beach') || l.includes('coast') || l.includes('seashore') || l.includes('shore')) return 'beach'
  if (l.includes('ocean') || l.includes('sea') || l.includes('wave') || l.includes('underwater')) return 'ocean'
  if (l.includes('forest') || l.includes('jungle') || l.includes('rainforest')) return 'forest'
  if (l.includes('lake') || l.includes('pond') || l.includes('river') || l.includes('waterfall')) return 'water'
  if (l.includes('desert') || l.includes('dune')) return 'desert'
  if (l.includes('sky') || l.includes('cloud') || l.includes('atmosphere')) return 'sky'
  if (l.includes('sunset') || l.includes('sunrise') || l.includes('dusk') || l.includes('dawn')) return 'golden-hour'
  if (l.includes('landscape') || l.includes('scenery') || l.includes('countryside') || l.includes('meadow')) return 'landscape'
  // Plants & greenery
  if (l.includes('plant') || l.includes('flower') || l.includes('herb') || l.includes('fern') ||
      l.includes('succulent') || l.includes('cactus') || l.includes('bonsai') || l.includes('foliage') ||
      l.includes('leaf') || l.includes('leaves') || l.includes('botanical') || l.includes('garden') ||
      l.includes('greenhouse') || l.includes('houseplant') || l.includes('potted')) return 'plant'
  // Art & creative
  if (l.includes('paint') || l.includes('artwork') || l.includes('canvas') || l.includes('mural') ||
      l.includes('portrait') || l.includes('sculpture') || l.includes('drawing') || l.includes('sketch') ||
      l.includes('illustration') || l.includes('gallery') || l.includes('museum') || l.includes('art')) return 'artwork'
  // Tech & workspace
  if (l.includes('laptop') || l.includes('computer') || l.includes('keyboard') || l.includes('monitor') ||
      l.includes('desk') || l.includes('workspace') || l.includes('office') || l.includes('studio')) return 'workspace'
  // Books & reading
  if (l.includes('book') || l.includes('library') || l.includes('reading') || l.includes('shelf') ||
      l.includes('bookcase') || l.includes('novel') || l.includes('magazine')) return 'books'
  // Food
  if (l.includes('pizza')) return 'pizza'
  if (l.includes('burger') || l.includes('hamburger')) return 'burger'
  if (l.includes('cake') || l.includes('pastry') || l.includes('dessert') || l.includes('bakery')) return 'cake'
  if (l.includes('coffee') || l.includes('espresso') || l.includes('latte') || l.includes('cappuccino')) return 'coffee'
  if (l.includes('food') || l.includes('dish') || l.includes('meal') || l.includes('cuisine') ||
      l.includes('restaurant') || l.includes('dining') || l.includes('kitchen')) return 'food'
  // Urban
  if (l.includes('city') || l.includes('street') || l.includes('building') || l.includes('skyline') ||
      l.includes('architecture') || l.includes('urban')) return 'urban'
  if (l.includes('cafe') || l.includes('bar') || l.includes('bistro')) return 'dining'
  // Indoor / home
  if (l.includes('room') || l.includes('bedroom') || l.includes('living') || l.includes('interior') ||
      l.includes('home') || l.includes('house') || l.includes('apartment') || l.includes('cozy')) return 'indoor'
  // Events
  if (l.includes('party') || l.includes('celebration') || l.includes('wedding') || l.includes('festival')) return 'celebration'
  // Animals
  if (l.includes('dog') || l.includes('cat') || l.includes('pet') || l.includes('animal') ||
      l.includes('bird') || l.includes('wildlife') || l.includes('horse') || l.includes('fish')) return 'animal'
  // Fashion
  if (l.includes('fashion') || l.includes('outfit') || l.includes('clothing') || l.includes('dress') ||
      l.includes('style') || l.includes('wardrobe') || l.includes('accessory')) return 'fashion'
  // Fitness
  if (l.includes('gym') || l.includes('fitness') || l.includes('workout') || l.includes('yoga') ||
      l.includes('sport') || l.includes('exercise') || l.includes('running')) return 'fitness'
  return 'lifestyle'
}

function determineMood(scene: string, colorMood: string, objects: string[]): string {
  if (['mountains', 'ocean', 'forest', 'beach'].includes(scene)) return 'peaceful'
  const personCount = objects.filter(o => o === 'person').length
  if (personCount >= 3) return 'social'
  if (personCount === 2) return 'intimate'
  return colorMood
}

function determineCategory(scene: string, objects: string[]): string {
  if (['mountains', 'ocean', 'forest', 'beach', 'sky'].includes(scene)) return 'nature'
  if (scene === 'urban') return 'city'
  if (scene === 'dining' || objects.includes('food')) return 'food'
  if (objects.some(o => ['person', 'people', 'man', 'woman'].includes(o))) return 'people'
  return 'lifestyle'
}

// Returns a downscaled canvas-based image element for TensorFlow
// Max 640px prevents OOM crashes on high-res iPhone photos
function createImageElement(file: File): Promise<HTMLImageElement> {
  const MAX_SIZE = 640
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        reject(new Error('Image has zero dimensions'))
        return
      }
      // Downscale if larger than MAX_SIZE
      const w = img.naturalWidth
      const h = img.naturalHeight
      if (w <= MAX_SIZE && h <= MAX_SIZE) {
        resolve(img)
        return
      }
      const scale = Math.min(MAX_SIZE / w, MAX_SIZE / h)
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(w * scale)
      canvas.height = Math.round(h * scale)
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      const resized = new Image()
      resized.onload = () => resolve(resized)
      resized.onerror = () => reject(new Error('Failed to create resized image'))
      resized.src = canvas.toDataURL('image/jpeg', 0.85)
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')) }
    img.src = url
  })
}

function normalizeClassName(className: string): string {
  const classMap: Record<string, string> = {
    'person': 'person', 'cell phone': 'phone', 'laptop': 'laptop', 'mouse': 'computer',
    'bottle': 'bottle', 'wine glass': 'glass', 'cup': 'cup', 'chair': 'chair',
    'couch': 'sofa', 'bed': 'bed', 'bicycle': 'bike', 'car': 'car',
    'motorcycle': 'motorcycle', 'bird': 'bird', 'cat': 'cat', 'dog': 'dog',
    'horse': 'horse', 'elephant': 'elephant', 'bear': 'bear', 'zebra': 'zebra',
    'book': 'book', 'clock': 'clock', 'vase': 'vase', 'potted plant': 'plant',
    'umbrella': 'umbrella', 'handbag': 'handbag', 'tie': 'tie', 'backpack': 'backpack',
    // COCO food classes — map to specific names
    'apple': 'apple', 'banana': 'banana', 'orange': 'orange', 'broccoli': 'broccoli',
    'carrot': 'carrot', 'hot dog': 'hot dog', 'pizza': 'pizza', 'donut': 'donut',
    'cake': 'cake', 'sandwich': 'sandwich', 'bowl': 'bowl', 'fork': 'fork',
    'knife': 'knife', 'spoon': 'spoon', 'dining table': 'dining table',
    'sports ball': 'ball', 'frisbee': 'frisbee',
    'skis': 'skis', 'snowboard': 'snowboard', 'kite': 'kite',
    'baseball bat': 'baseball bat', 'baseball glove': 'baseball glove',
    'skateboard': 'skateboard', 'surfboard': 'surfboard', 'tennis racket': 'tennis racket',
    'traffic light': 'traffic light', 'fire hydrant': 'fire hydrant',
    'stop sign': 'stop sign', 'parking meter': 'parking meter', 'bench': 'bench',
    'tv': 'TV', 'microwave': 'microwave', 'oven': 'oven', 'toaster': 'toaster',
    'sink': 'sink', 'refrigerator': 'refrigerator', 'scissors': 'scissors',
    'teddy bear': 'teddy bear', 'hair drier': 'hair dryer', 'toothbrush': 'toothbrush',
    'truck': 'truck', 'bus': 'bus', 'train': 'train', 'airplane': 'airplane', 'boat': 'boat'
  }
  return classMap[className] || className.replace(/\s+/g, ' ').toLowerCase()
}

// Analyze dominant colors to infer content COCO-SSD can't detect
function inferContentFromColors(imageElement: HTMLImageElement, cocoKeywords: string[]): string[] {
  const inferred: string[] = []
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 64
    canvas.height = 64
    const ctx = canvas.getContext('2d')
    if (!ctx) return inferred
    ctx.drawImage(imageElement, 0, 0, 64, 64)
    
    let data: ImageData
    try {
      data = ctx.getImageData(0, 0, 64, 64)
    } catch {
      return inferred // Canvas tainted or security error
    }

    let redPixels = 0, greenPixels = 0, yellowPixels = 0, orangePixels = 0
    let brownPixels = 0, bluePixels = 0, pinkPixels = 0, totalPixels = 0

    for (let i = 0; i < data.data.length; i += 4) {
      const r = data.data[i], g = data.data[i + 1], b = data.data[i + 2]
      const brightness = (r + g + b) / 3
      if (brightness < 20 || brightness > 240) continue
      totalPixels++
      if (r > 150 && r > g * 1.8 && r > b * 1.8) redPixels++
      else if (g > 100 && g > r * 1.3 && g > b * 1.2) greenPixels++
      else if (r > 180 && g > 160 && b < 80) yellowPixels++
      else if (r > 180 && g > 80 && g < 160 && b < 80) orangePixels++
      else if (r > 80 && r < 180 && g > 40 && g < 130 && b < 80 && r > g && g > b) brownPixels++
      else if (b > 120 && b > r * 1.3 && b > g * 1.1) bluePixels++
      else if (r > 150 && b > 100 && g < 120) pinkPixels++
    }

    if (totalPixels < 100) return inferred
    const pct = (n: number) => n / totalPixels

    const hasFood = cocoKeywords.some(k => ['apple', 'banana', 'orange', 'pizza', 'cake', 'donut', 'sandwich', 'carrot', 'broccoli'].includes(k))

    // Red + green background = strawberry
    if (pct(redPixels) > 0.12 && pct(greenPixels) > 0.15 && !hasFood) inferred.push('strawberry')
    // Red dominant, no green = apple or tomato
    else if (pct(redPixels) > 0.2 && !hasFood) inferred.push('red fruit')
    // Yellow = banana or lemon
    if (pct(yellowPixels) > 0.2 && !hasFood) inferred.push('banana')
    // Orange = orange fruit
    if (pct(orangePixels) > 0.15 && !hasFood) inferred.push('orange')
    // Heavy green = plant/nature
    if (pct(greenPixels) > 0.4 && !cocoKeywords.includes('plant')) inferred.push('nature')
    // Brown = coffee/wood/earth
    if (pct(brownPixels) > 0.35 && !cocoKeywords.some(k => ['cup', 'bowl'].includes(k))) inferred.push('earthy')
    // Blue = sky or water
    if (pct(bluePixels) > 0.3) inferred.push('sky')
    // Pink/purple = flowers
    if (pct(pinkPixels) > 0.2) inferred.push('flowers')

    console.log('🎨 Color inference:', { pct_red: pct(redPixels).toFixed(2), pct_green: pct(greenPixels).toFixed(2), pct_yellow: pct(yellowPixels).toFixed(2), inferred })
  } catch (e) {
    console.warn('Color inference failed:', e)
  }
  return inferred
}

export function isEnhancedModelReady(): boolean {
  return cocoModel !== null && mobileNetModel !== null
}
